// Sidechain verb — duck a "source" signal by the envelope of a "key"
// signal. Most common use: kick drum (key) ducks bass / pad (source) so
// the kick "punches through" the mix. Output goes to the forge's commit
// pipeline like any other verb.

import { useCallback, useState } from 'react';
import { applySidechain } from './sidechain';
import { fromPcm, toPcm } from '../../pipeline';
import { Slider } from '../Slider';
import { SourcePicker, type PickedSource } from '../SourcePicker';
import type { VerbProps } from '../types';

export function SidechainVerb({
  audioContext,
  onBuffer,
  onStatus,
}: VerbProps) {
  const [source, setSource] = useState<PickedSource | null>(null);
  const [key, setKey] = useState<PickedSource | null>(null);
  const [amount, setAmount] = useState(0.7);
  const [releaseMs, setReleaseMs] = useState(120);
  const [rendering, setRendering] = useState(false);

  const handleRender = useCallback(async () => {
    if (!source || !key || !audioContext) return;
    setRendering(true);
    try {
      const result = applySidechain(
        toPcm(source.buffer),
        toPcm(key.buffer),
        { amount, releaseMs },
      );
      const buffer = fromPcm(audioContext, result);
      const sourceIds = [source.sampleId, key.sampleId].filter(
        (id): id is string => typeof id === 'string',
      );
      onBuffer(buffer, {
        suggestedName: `${source.name}_ducked`,
        origin: {
          kind: 'sidechain',
          sourceSampleId: source.sampleId,
          keySampleId: key.sampleId,
          amount,
          releaseMs,
        },
        sourceSampleIds: sourceIds.length > 0 ? sourceIds : undefined,
      });
      onStatus('Rendered — trim and save.', 'ok');
    } catch (e) {
      onStatus(
        'Render failed: ' + (e instanceof Error ? e.message : String(e)),
        'err',
      );
    } finally {
      setRendering(false);
    }
  }, [source, key, audioContext, amount, releaseMs, onBuffer, onStatus]);

  return (
    <div className="flex flex-col gap-3 min-h-[200px]">
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-400">
        Duck the source by the envelope of the key signal. Try a kick as the
        key and a pad / bass as the source.
      </div>
      <SourcePicker
        audioContext={audioContext}
        selectedId={source?.sampleId}
        onPicked={setSource}
        onStatus={onStatus}
        label="Source (the signal that gets ducked)"
      />
      <SourcePicker
        audioContext={audioContext}
        selectedId={key?.sampleId}
        onPicked={setKey}
        onStatus={onStatus}
        label="Key (drives the envelope)"
      />
      <Slider
        label="Amount"
        value={amount}
        min={0}
        max={1}
        step={0.01}
        display={amount.toFixed(2)}
        onChange={setAmount}
        displayWidth="narrow"
      />
      <Slider
        label="Release"
        value={releaseMs}
        min={5}
        max={500}
        step={5}
        display={`${releaseMs} ms`}
        onChange={setReleaseMs}
        displayWidth="narrow"
      />
      <button
        type="button"
        onClick={() => void handleRender()}
        disabled={!source || !key || rendering}
        className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
      >
        {rendering ? 'Rendering…' : '● Render'}
      </button>
    </div>
  );
}

